import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "targetType",
    "gradeSelect",
    "classroomSelect",
    "groupSelect",
    "studentSelect",
    "instrumentRadio",
    "followUpSection",
    "frequency",
    "onceSection",
    "roundsSection",
    "roundRow",
  ];

  connect() {
    this.toggleFollowUp();
    this.toggleSchedule();
    // Defer so a sibling tom-select controller has built its instance first —
    // toggling the raw <select>'s disabled attribute doesn't update the
    // tom-select control, so we enable/disable the instance instead.
    requestAnimationFrame(() => this.toggleTargets());
  }

  // Switch between the single "Once" dates and the per-round "2×"/"3×" schedule.
  // Inputs in the inactive schedule are disabled so they don't submit.
  toggleSchedule() {
    if (!this.hasFrequencyTarget) return;

    const frequency = this.frequencyTarget.value;
    const once = frequency === "once";
    const roundCount =
      frequency === "termly" ? 3 : frequency === "biannual" ? 2 : 0;

    if (this.hasOnceSectionTarget)
      this.setSection(this.onceSectionTarget, once);
    if (this.hasRoundsSectionTarget) {
      this.roundsSectionTarget.classList.toggle("hidden", once);
      this.roundRowTargets.forEach((row, i) =>
        this.setSection(row, i < roundCount),
      );
    }
  }

  // Show/hide a section and enable/disable its inputs so hidden fields don't post.
  setSection(section, active) {
    section.classList.toggle("hidden", !active);
    section.querySelectorAll("input, select").forEach((el) => {
      // A tom-select-enhanced <select> must be driven through its instance;
      // setting the raw disabled attribute leaves the visible control stuck.
      if (el.tomselect) {
        if (active) {
          el.tomselect.enable();
        } else {
          el.tomselect.disable();
        }
      } else {
        el.disabled = !active;
      }
    });
  }

  // Show the weekly auto-follow-up option only for clinical screeners that define
  // a follow-up rule.
  toggleFollowUp() {
    if (
      !this.hasFollowUpSectionTarget ||
      this.instrumentRadioTargets.length === 0
    )
      return;

    const checked = this.instrumentRadioTargets.find((radio) => radio.checked);
    const supportsFollowUp = checked?.dataset.followUp === "true";
    this.followUpSectionTarget.classList.toggle("hidden", !supportsFollowUp);
  }

  toggleTargets() {
    if (!this.hasTargetTypeTarget) return;

    const value = this.targetTypeTarget.value;
    const sections = {
      grades: this.hasGradeSelectTarget ? this.gradeSelectTarget : null,
      classrooms: this.hasClassroomSelectTarget
        ? this.classroomSelectTarget
        : null,
      groups: this.hasGroupSelectTarget ? this.groupSelectTarget : null,
      students: this.hasStudentSelectTarget ? this.studentSelectTarget : null,
    };

    // Only the active section is shown and submits; the rest are hidden and
    // their inputs disabled so a stale pick from another type can't tag along.
    Object.entries(sections).forEach(([type, section]) => {
      if (section) this.setSection(section, value === type);
    });
  }
}
